import { Formik, Field, Form } from "formik";
import { useUniqueId } from "shared/uniqueId/useUniqueId";
import { useTranslation } from "shared/i18n/useTranslation";
import { SiteSurvey } from "api/types/siteSurvey";
import { SiteSurveyConfiguration } from "api/types/siteSurveyConfiguration";
import { AddressSection } from "./AddressSection";
import { Photos } from "./Photos";
import { useSurveyForm } from "./helpers/useSurveyForm";
import { AddEditUnit } from "./unit/AddEditUnit";
import { Units } from "./Units";
import { Buttons } from "./Buttons";
import { Photo } from "api/types/photo";
import { BreakerPanelAmperage } from "./BreakerPanelAmperage";
import { BulkEditUnits } from "./BulkEditUnits";
import { SiteAttributes } from "./SiteAttributes";

import { nameof } from "@sprint1/pkg/src/ts-utils/nameof";
const name = nameof<SiteSurvey>;

export interface SurveyFormType {
  survey: SiteSurvey;
  config: SiteSurveyConfiguration;
  action: "editSurvey" | "editUnit";
  unitId: number | null | undefined;
}

export function SurveyForm({ survey, config, action, unitId }: SurveyFormType) {
  return (
    <div>
      <Formik initialValues={survey} onSubmit={() => {}}>
        <SurveyFormChildren config={config} action={action} unitId={unitId} />
      </Formik>
    </div>
  );
}

function SurveyFormChildren({
  config,
  action,
  unitId,
}: Pick<SurveyFormType, "config" | "action" | "unitId">) {
  const { handleSubmit } = useSurveyForm();
  return (
    <Form onSubmit={handleSubmit}>
      {action === "editSurvey" && <SurveyEditSection config={config} />}
      {action === "editUnit" && <AddEditUnit unitId={unitId} config={config} />}
    </Form>
  );
}

function SurveyEditSection({ config }: { config: SiteSurveyConfiguration }) {
  const { uid } = useUniqueId("SurveyForm");
  const { translate } = useTranslation();
  const { survey, setValues } = useSurveyForm();

  function handleOnPhotosChange(photos) {
    survey.photos = [...photos];
    setValues(survey);
  }

  function handleDeletePhoto(photo: Photo) {
    const newPhotos = survey.photos.filter((p) => p !== photo);
    survey.photos = newPhotos;
    setValues(survey);
  }
  return (
    <>
      <div className="mb-3">
        <label htmlFor={uid("companyName")} className="form-label">
          {translate("__siteName")}
        </label>
        <Field
          className="form-control"
          id={uid("companyName")}
          name="clientName"
        />
      </div>

      <div className="mb-3">
        <label htmlFor={uid("customer")} className="form-label">
          {translate("__customer")}
        </label>
        <Field className="form-control" id={uid("customer")} name="customer" />
      </div>

      <AddressSection uid={uid} />
      <div className="my-3">
        <SiteAttributes config={config} />
      </div>
      <Photos
        photoTypes={config.photoTypes}
        photos={survey.photos}
        onChange={handleOnPhotosChange}
        onDelete={handleDeletePhoto}
      />

      <Units />

      <div className="mt-3">
        <BulkEditUnits config={config} />
      </div>

      <div className="mt-3">
        <label htmlFor={uid("comments")} className="form-label">
          {translate("__siteNotesOrComments")}
        </label>
        <Field name={name("comments")}>
          {({ field }) => (
            <textarea
              {...field}
              rows={5}
              className="form-control"
              id={uid("comments")}
              value={field.value || ""}
            />
          )}
        </Field>
      </div>

      <Buttons />
    </>
  );
}
